import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { X, Plus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface BulkTagDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mediaIds: string[];
}

export const BulkTagDialog = ({ open, onOpenChange, mediaIds }: BulkTagDialogProps) => {
  const [inputValue, setInputValue] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const queryClient = useQueryClient();

  const { data: allTags } = useQuery({
    queryKey: ['media-tags'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('media_tags')
        .select('*')
        .order('name');
      
      if (error) throw error;
      return data;
    },
  });

  const addTagsMutation = useMutation({
    mutationFn: async (tagNames: string[]) => {
      for (const tagName of tagNames) {
        // Find or create tag
        let tagId: string;
        const existingTag = allTags?.find(t => t.name.toLowerCase() === tagName.toLowerCase());
        
        if (existingTag) {
          tagId = existingTag.id;
        } else {
          const { data, error } = await supabase
            .from('media_tags')
            .insert({ name: tagName })
            .select()
            .single();
          
          if (error) throw error;
          tagId = data.id;
        }

        // Add tag to all selected media
        for (const mediaId of mediaIds) {
          await supabase
            .from('media_tag_relations')
            .upsert({ media_id: mediaId, tag_id: tagId }, { onConflict: 'media_id,tag_id' });
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media-library'] });
      queryClient.invalidateQueries({ queryKey: ['media-tags'] });
      onOpenChange(false);
      setSelectedTags([]);
      toast({ 
        title: "Success",
        description: `Added tags to ${mediaIds.length} item(s)` 
      });
    },
  });

  const handleAddTag = () => {
    if (!inputValue.trim() || selectedTags.includes(inputValue.trim())) return;
    setSelectedTags([...selectedTags, inputValue.trim()]);
    setInputValue("");
  };

  const handleRemoveTag = (tag: string) => {
    setSelectedTags(selectedTags.filter(t => t !== tag));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Tags to {mediaIds.length} Item(s)</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {selectedTags.map(tag => (
              <Badge key={tag} variant="secondary" className="gap-1">
                {tag}
                <button onClick={() => handleRemoveTag(tag)}>
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>

          <div className="flex gap-2">
            <Input
              placeholder="Add tag..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddTag();
                }
              }}
            />
            <Button size="sm" onClick={handleAddTag} disabled={!inputValue.trim()}>
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={() => addTagsMutation.mutate(selectedTags)}
            disabled={selectedTags.length === 0 || addTagsMutation.isPending}
          >
            Add Tags
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
